import type Luminary from '@lumen5/luminary';

import type { AssetContainerModel } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';

import { generateRegistryId } from './generateRegistryId';

export interface ItemRepresentation {
    id: string;
    type: string;
    title: string;
    description: string;
    tags: string;
}

export interface ItemEntry<T extends ItemRepresentation> {
    id: string;
    data: AssetContainerModel;
    representation: T;
}

export interface SerializedRegistryItem<T extends ItemRepresentation> {
    id: string;
    uuid: string;
    representation: T;
    data: AssetContainerModel;
}

export interface SerializedRegistry<T extends ItemRepresentation> {
    items: SerializedRegistryItem<T>[];
}

export abstract class BaseRegistry<T extends ItemRepresentation> {
    protected registry: Map<string, ItemEntry<T>> = new Map();

    protected generateId(): string {
        return generateRegistryId((id) => this.registry.has(id));
    }

    // Abstract methods that subclasses must implement
    protected abstract extractItemInfo(data: AssetContainerModel): {
        title: string;
        description: string;
        tags: string[];
    };

    protected abstract validateItemData(data: AssetContainerModel): void;

    protected abstract createRepresentation(
        id: string,
        itemInfo: { title: string; description: string; tags: string[] },
        data: AssetContainerModel,
    ): T;

    protected abstract getItemUuid(data: AssetContainerModel): string | undefined;

    protected abstract isItemAlreadyInRegistry(uuid: string): string | null;

    public abstract addAnyUnseenItemsFromVideo(luminary: Luminary): Promise<void>;

    // Common implementation methods
    public addItem(data: AssetContainerModel): string {
        this.validateItemData(data);

        const uuid = this.getItemUuid(data);
        if (!uuid) {
            throw new Error('Invalid item data: must contain a UUID');
        }

        // Check if item already exists
        const existingId = this.isItemAlreadyInRegistry(uuid);
        if (existingId) {
            return existingId;
        }

        // Create a new item entry
        const id = this.generateId();
        const itemInfo = this.extractItemInfo(data);
        const representation = this.createRepresentation(id, itemInfo, data);

        const entry: ItemEntry<T> = {
            id,
            data,
            representation,
        };

        this.registry.set(id, entry);
        return id;
    }

    public getItem(id: string): AssetContainerModel | null {
        const entry = this.registry.get(id);
        return entry ? entry.data : null;
    }

    /**
     * The asset uuid behind a registry id. The agent only ever sees the short registry
     * id, so this is how the UI recovers the uuid a link needs.
     */
    public getUuidByItemId(id: string): string | undefined {
        const entry = this.registry.get(id);
        return entry ? this.getItemUuid(entry.data) : undefined;
    }

    public getRepresentation(id: string): T | null {
        const entry = this.registry.get(id);
        return entry ? entry.representation : null;
    }

    public hasItem(id: string): boolean {
        return this.registry.has(id);
    }

    public getAllRepresentations(): T[] {
        return Array.from(this.registry.values()).map((entry) => entry.representation);
    }

    public clear(): void {
        this.registry.clear();
    }

    /** Serializes the registry to a JSON-compatible object for persistence. */
    public serialize(): SerializedRegistry<T> {
        const items: SerializedRegistryItem<T>[] = [];
        for (const [id, entry] of this.registry.entries()) {
            const uuid = this.getItemUuid(entry.data);
            if (uuid) {
                items.push({ id, uuid, representation: entry.representation, data: entry.data });
            }
        }
        return { items };
    }

    /** Loads serialized data into the registry, replacing any existing data. */
    public loadFromSerialized(serialized: SerializedRegistry<T>): void {
        this.clear();
        for (const item of serialized.items) {
            this.registry.set(item.id, {
                id: item.id,
                data: item.data,
                representation: item.representation,
            });
        }
    }

    public getItemIdByUuid(uuid: string): string | null {
        for (const [id, entry] of this.registry.entries()) {
            if (this.getItemUuid(entry.data) === uuid) {
                return id;
            }
        }
        return null;
    }

    public async addItemByUuid(uuid: string): Promise<string | null> {
        const [id] = await this.addItemsByUuids([uuid]);
        return id ?? null;
    }

    /**
     * Resolves several UUIDs in one pass, returning their registry ids positionally (null for
     * any that couldn't be resolved). Prefer this over calling `addItemByUuid` in a loop:
     * `fetchItemsFromApi` takes the whole set at once, so one call replaces N round-trips.
     */
    public async addItemsByUuids(uuids: string[]): Promise<(string | null)[]> {
        const unseenUuids = new Set(uuids.filter((uuid) => !this.isItemAlreadyInRegistry(uuid)));

        if (unseenUuids.size > 0) {
            const items = await this.fetchItemsFromApi(unseenUuids);
            items.forEach((item) => this.addItem(item));
        }

        return uuids.map((uuid) => this.getItemIdByUuid(uuid));
    }

    protected async fetchItemsFromApi(unseenUuids: Set<string>): Promise<AssetContainerModel[]> {
        if (unseenUuids.size === 0) {
            return [];
        }

        const uuidsString = Array.from(unseenUuids).join(',');
        try {
            const response = await ApiService.bulkMediaAssetsList({
                query: {
                    uuids: uuidsString,
                },
            });

            return response.data || [];
        } catch (error) {
            ErrorReporter.capture(error);
            return [];
        }
    }
}
